import type { RenDecorationComponent } from '../../types'
import { formatMoney } from '../composables/useDecorationDataSource'

export { formatMoney }

export function stringValue(value: unknown, fallback = ''): string {
  if (value === undefined || value === null) return fallback
  return String(value)
}

export function numberValue(value: unknown, fallback = 0): number {
  const num = Number(value)
  return isNaN(num) ? fallback : num
}

export function booleanValue(value: unknown, fallback = false): boolean {
  if (value === undefined || value === null) return fallback
  return Boolean(value)
}

export function recordList(value: unknown): Record<string, unknown>[] {
  if (!Array.isArray(value)) return []
  return value.filter((item): item is Record<string, unknown> => typeof item === 'object' && item !== null)
}

export function previewDataRecord(data: unknown): Record<string, unknown> {
  if (typeof data === 'object' && data !== null && !Array.isArray(data)) {
    return data as Record<string, unknown>
  }
  return {}
}

export function previewRecordsFor(
  component: RenDecorationComponent,
  componentData: unknown,
  fallback: Record<string, unknown>[] = []
): Record<string, unknown>[] {
  const bindings = (component.dataSource as { bindings?: Record<string, string> } | null)?.bindings ?? {}
  const recordsBinding = bindings.records || bindings.items
  if (recordsBinding) {
    const records = previewValueByPath(componentData, recordsBinding)
    if (Array.isArray(records)) return recordList(records)
  }
  if (Array.isArray(componentData)) return recordList(componentData)
  const record = previewDataRecord(componentData)
  for (const key of ['records', 'items', 'data']) {
    if (Array.isArray(record[key])) return recordList(record[key])
  }
  return fallback
}

export function previewValueByPath(source: unknown, path: string): unknown {
  const normalized = path.replace(/\[\]/g, '').replace(/^(item|data)\./, '')
  const read = (value: unknown) =>
    normalized
      .split('.')
      .filter(Boolean)
      .reduce<unknown>((current, key) => {
        return current && typeof current === 'object' ? (current as Record<string, unknown>)[key] : undefined
      }, value)
  return read(source) ?? read(previewDataRecord(source))
}

export function previewBoundValue(
  component: RenDecorationComponent,
  componentData: unknown,
  target: string,
  fallback: unknown,
  item?: Record<string, unknown>
): unknown {
  const bindings = (component.dataSource as { bindings?: Record<string, string> } | null)?.bindings ?? {}
  const path = bindings[target] || target
  const normalized = path.replace(/^(records|items|data)\[\]\./, '').replace(/^(item|data)\./, '')
  const source = item ?? previewDataRecord(componentData)
  const value = normalized
    .split('.')
    .filter(Boolean)
    .reduce<unknown>((current, key) => {
      return current && typeof current === 'object' ? (current as Record<string, unknown>)[key] : undefined
    }, source)
  return value ?? fallback
}

export function previewBoundString(
  component: RenDecorationComponent,
  componentData: unknown,
  target: string,
  fallback = '',
  item?: Record<string, unknown>
): string {
  return stringValue(previewBoundValue(component, componentData, target, fallback, item), fallback)
}

/** 宫格 icon 是短字，不是英文标识。长 ascii 名回退到标题首字。 */
export function previewServiceIconText(value: string, fallback = ''): string {
  const raw = value.trim()
  const fallbackGlyph = fallback.trim().slice(0, 1)
  if (!raw) return fallbackGlyph
  if (raw.length <= 2) return raw
  if (/^[a-z0-9_-]+$/i.test(raw)) return fallbackGlyph || raw.slice(0, 1)
  return raw.slice(0, 1)
}

export function styleColorList(value: unknown): string[] {
  const colors = Array.isArray(value) ? value.map((item) => stringValue(item)) : stringValue(value).split(',')
  return colors.map((item) => item.trim()).filter((item) => /^#[0-9a-fA-F]{6}$/.test(item))
}
